"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
  SymbolChart,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  Notice,
  type Option,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type A1Z26LetterSeparator,
  type A1Z26Options,
  type A1Z26WordSeparator,
  a1z26Decode,
  a1z26Encode,
  a1z26Number,
  DEFAULT_A1Z26_OPTIONS,
} from "@/lib/tool-kit/ciphers/a1z26";
import { ALPHABET, lettersOnly } from "@/lib/tool-kit/ciphers/alphabet";

/** English strings. A translated content file overrides these through `ui`. */
export const A1Z26_CIPHER_STRINGS = {
  direction: "Convert",
  encode: "Letters → numbers",
  decode: "Numbers → letters",
  textInput: "Text",
  numberInput: "Numbers",
  textOutput: "Letters",
  numberOutput: "Numbers",
  inputPlaceholder: "Type text, or paste numbers such as 8-5-12-12-15",
  sample: "Hello World",
  letterSeparator: "Between letters",
  dash: "Dash (8-5-12)",
  space: "Space (8 5 12)",
  comma: "Comma (8,5,12)",
  wordSeparator: "Between words",
  slash: "Slash (8-9 / 20-15)",
  doubleSpace: "Double space",
  reverse: "Reverse alphabet (Z = 1, A = 26)",
  zeroBased: "Count from zero (A = 0)",
  keepPunctuation: "Keep punctuation marks such as ! ? ' ( )",
  encodeRule:
    "Letters become numbers and each space becomes the word separator. Digits and letters outside A–Z are skipped. Commas, dashes and full stops are always dropped, because they separate numbers; other punctuation is kept only if you tick the box.",
  decodeRule:
    "Each run of digits is one number. A slash or two spaces start a new word, a line break starts a new line, and dashes, commas, full stops and single spaces just separate numbers. Other punctuation is copied through.",
  skipped: "Skipped — no number in A1Z26: {chars}",
  invalid:
    "No letter for {numbers}: with these settings the numbers run from {first} to {last}, so they appear as ?.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "Letter numbers",
  chartLabel: "Each letter and its number",
  chartHint: "Letters in your message are highlighted.",
} as const;

export default function A1z26CipherWidget() {
  const strings = useToolUi(A1Z26_CIPHER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [options, setOptions] = useState<A1Z26Options>(DEFAULT_A1Z26_OPTIONS);

  const encoded = mode === "encode" ? a1z26Encode(input, options) : null;
  const decoded = mode === "decode" ? a1z26Decode(input, options) : null;
  const output = encoded?.output ?? decoded?.output ?? "";
  const used = new Set(lettersOnly(mode === "encode" ? input : output));
  const first = options.zeroBased ? 0 : 1;

  const update = (patch: Partial<A1Z26Options>) =>
    setOptions((current) => ({ ...current, ...patch }));

  const letterOptions: Option<A1Z26LetterSeparator>[] = [
    { value: "-", label: strings.dash },
    { value: " ", label: strings.space },
    { value: ",", label: strings.comma },
  ];
  const wordOptions: Option<A1Z26WordSeparator>[] = [
    { value: " / ", label: strings.slash },
    { value: "  ", label: strings.doubleSpace },
  ];

  const keyControls = (
    <div className="space-y-4">
      {mode === "encode" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="az-letters"
            label={strings.letterSeparator}
            onChange={(value) => update({ letterSeparator: value })}
            options={letterOptions}
            value={options.letterSeparator}
          />
          <SelectField
            id="az-words"
            label={strings.wordSeparator}
            onChange={(value) => update({ wordSeparator: value })}
            options={wordOptions}
            value={options.wordSeparator}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={options.reverse}
          label={strings.reverse}
          onChange={(checked) => update({ reverse: checked })}
        />
        <Checkbox
          checked={options.zeroBased}
          label={strings.zeroBased}
          onChange={(checked) => update({ zeroBased: checked })}
        />
        {mode === "encode" ? (
          <Checkbox
            checked={options.keepPunctuation}
            label={strings.keepPunctuation}
            onChange={(checked) => update({ keepPunctuation: checked })}
          />
        ) : null}
      </div>
      <p className={HINT}>
        {mode === "encode" ? strings.encodeRule : strings.decodeRule}
      </p>
    </div>
  );

  const notices = (
    <>
      {encoded && encoded.skipped.length > 0 ? (
        <Notice>
          {t(strings.skipped, { chars: encoded.skipped.join(" ") })}
        </Notice>
      ) : null}
      {decoded && decoded.invalid.length > 0 ? (
        <Notice>
          {t(strings.invalid, {
            numbers: decoded.invalid.join(", "),
            first: fmt(first),
            last: fmt(first + 25),
          })}
        </Notice>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="az"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.direction,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.textInput : strings.numberInput,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.numberOutput : strings.textOutput,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      working={
        <div className="space-y-2">
          <SymbolChart
            items={[...ALPHABET].map((letter, index) => ({
              key: letter,
              value: String(a1z26Number(index, options)),
              active: used.has(letter),
            }))}
            label={strings.chartLabel}
          />
          <p className={HINT}>{strings.chartHint}</p>
        </div>
      }
    />
  );
}
